import { AsyncPipe } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { EventType, NavigationEnd, Router } from '@angular/router';
import { VexPopoverService } from '@vex/components/vex-popover/vex-popover.service';
import { VexScrollbarComponent } from '@vex/components/vex-scrollbar/vex-scrollbar.component';
import { VexConfigService } from '@vex/config/vex-config.service';
import { VexLayoutService } from '@vex/services/vex-layout.service';
import { Observable, of } from 'rxjs';
import { filter, map, startWith, switchMap } from 'rxjs/operators';
import { NavigationItem } from '../../../core/navigation/navigation-item.interface';
import { NavigationService } from '../../../core/navigation/navigation.service';
import { SearchModalComponent } from './search-modal/search-modal.component';
import { SidenavItemComponent } from './sidenav-item/sidenav-item.component';
import { SidenavUserMenuComponent } from './sidenav-user-menu/sidenav-user-menu.component';

@Component({
  selector: 'vex-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  imports: [MatButtonModule, MatIconModule, MatRippleModule, VexScrollbarComponent, SidenavItemComponent, AsyncPipe],
})
export class SidenavComponent implements OnInit {
  @Input() collapsed: boolean = false;

  private router = inject(Router);

  collapsedOpen$ = this.layoutService.sidenavCollapsedOpen$;
  title$ = this.configService.config$.pipe(map((config) => config.sidenav.title));
  imageUrl$ = this.configService.config$.pipe(map((config) => config.sidenav.imageUrl));
  showCollapsePin$ = this.configService.config$.pipe(map((config) => config.sidenav.showCollapsePin));
  userVisible$ = this.configService.config$.pipe(map((config) => config.sidenav.user.visible));
  searchVisible$ = this.configService.config$.pipe(map((config) => config.sidenav.search.visible));

  userMenuOpen$: Observable<boolean> = of(false);

  items$!: Observable<NavigationItem[]>;
  isAdmin = false;
  isInternal = false;

  constructor(
    private navigationService: NavigationService,
    private layoutService: VexLayoutService,
    private configService: VexConfigService,
    private readonly popoverService: VexPopoverService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.router.events
      .pipe(
        filter((event) => event.type === EventType.NavigationEnd),
        map((event) => (event as NavigationEnd).url),
        startWith(this.router.routerState.snapshot.url),
      )
      .subscribe({
        next: (url) => {
          if (url.startsWith('/internal')) {
            this.items$ = this.navigationService.itemsInternal$;
            this.isAdmin = false;
            this.isInternal = true;
          } else if (url.startsWith('/admin')) {
            this.items$ = this.navigationService.itemsAdmin$;
            this.isAdmin = true;
            this.isInternal = false;
          } else {
            this.items$ = this.navigationService.items$;
            this.isAdmin = false;
            this.isInternal = false;
          }
        },
      });
  }

  collapseOpenSidenav() {
    this.layoutService.collapseOpenSidenav();
  }

  collapseCloseSidenav() {
    this.layoutService.collapseCloseSidenav();
  }

  toggleCollapse() {
    this.collapsed ? this.layoutService.expandSidenav() : this.layoutService.collapseSidenav();
  }

  trackByRoute(index: number, item: NavigationItem): string {
    if (item.type === 'link') {
      return item.route;
    }

    return item.label;
  }

  openProfileMenu(origin: HTMLDivElement): void {
    this.userMenuOpen$ = of(
      this.popoverService.open({
        content: SidenavUserMenuComponent,
        origin,
        offsetY: -8,
        width: origin.clientWidth,
        position: [
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
          },
        ],
      }),
    ).pipe(
      switchMap((popoverRef) => popoverRef.afterClosed$.pipe(map(() => false))),
      startWith(true),
    );
  }

  openSearch(): void {
    this.dialog.open(SearchModalComponent, {
      panelClass: 'vex-dialog-glossy',
      width: '100%',
      maxWidth: '600px',
    });
  }
}
